import { Dispatch } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';

import { supabase } from '../lib/supabaseClient';
import { logout } from '../redux/slices/authSlice';
import { resetPortfolio } from '../redux/slices/portfolioSlice';
import { resetSites } from '../redux/slices/siteSlice';

type LogoutToastType = 'success' | 'error';

interface LogoutOptions {
  toastMessage?: string;
  toastType?: LogoutToastType;
  skipSupabaseSignOut?: boolean;
  suppressToastOnPublicAuthRoutes?: boolean;
}

let isGlobalLogoutInProgress = false;

const clearBrowserStorage = async () => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.clear();
  } catch (error) {
    console.warn('Failed to clear localStorage during logout:', error);
  }

  try {
    sessionStorage.clear();
  } catch (error) {
    console.warn('Failed to clear sessionStorage during logout:', error);
  }

  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
    } catch (error) {
      console.warn('Failed to clear Cache Storage during logout:', error);
    }
  }
};

export const performGlobalLogout = async (
  dispatch: Dispatch,
  options: LogoutOptions = {}
) => {
  if (isGlobalLogoutInProgress) {
    return;
  }

  isGlobalLogoutInProgress = true;

  const {
    toastMessage,
    toastType = 'success',
    skipSupabaseSignOut = false,
    suppressToastOnPublicAuthRoutes = true,
  } = options;

  const isOnPublicAuthRoute = () => {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname.toLowerCase();
    const publicRoutes = ['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
    return publicRoutes.some((route) => path.startsWith(route));
  };

  try {
    if (!skipSupabaseSignOut) {
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        console.warn('Supabase signOut during logout failed:', signOutError);
      }
    }

    dispatch(logout());
    
    // Reset portfolio and site slices to clear cached data
    dispatch(resetPortfolio());
    dispatch(resetSites());

    // Reset RTK Query cache to ensure fresh data on next login
    dispatch({ type: 'api/util/resetApiState' });

    await clearBrowserStorage();

    const shouldShowToast =
      toastMessage &&
      !(suppressToastOnPublicAuthRoutes && isOnPublicAuthRoute());

    if (shouldShowToast) {
      if (toastType === 'error') {
        try {
          toast.error(toastMessage);
        } catch (error) {
          console.warn('Failed to show toast during logout:', error);
        }
      } else {
      }
    }
  } finally {
    // Allow future logout attempts after a short delay to avoid race conditions
    setTimeout(() => {
      isGlobalLogoutInProgress = false;
    }, 500);
  }
};


